import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-service-agreement',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="service-agreement">
      <div class="container">
        <header class="page-header">
          <h1>Service Level Agreement (SLA)</h1>
          <p class="last-updated">Last Updated: {{ lastUpdated | date:'longDate' }}</p>
        </header>

        <div class="content">
          <div class="section">
            <h2>1. Service Commitment</h2>
            <p>
              Industry Intelligence is committed to providing a platform availability
              of 99.9% during each calendar month. If we do not meet this SLA, you
              may be eligible for service credits as described below.
            </p>
          </div>

          <div class="section">
            <h2>2. Definitions</h2>
            <div class="definitions">
              <div class="definition">
                <h3>2.1 "Monthly Uptime Percentage"</h3>
                <p>
                  The total number of minutes in a month, minus the number of minutes
                  of Downtime during that month, divided by the total number of
                  minutes in the month.
                </p>
              </div>

              <div class="definition">
                <h3>2.2 "Downtime"</h3>
                <p>
                  Any period during which the platform is unavailable as measured by
                  our monitoring systems. Scheduled maintenance does not count as downtime.
                </p>
              </div>
            </div>
          </div>

          <div class="section">
            <h2>3. Service Credits</h2>
            <div class="service-credits">
              <table>
                <thead>
                  <tr>
                    <th>Monthly Uptime Percentage</th>
                    <th>Service Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Less than 99.9% but equal to or greater than 99.0%</td>
                    <td>10% of monthly fee</td>
                  </tr>
                  <tr>
                    <td>Less than 99.0% but equal to or greater than 95.0%</td>
                    <td>25% of monthly fee</td>
                  </tr>
                  <tr>
                    <td>Less than 95.0%</td>
                    <td>100% of monthly fee</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="section">
            <h2>4. Support Response Times</h2>
            <div class="support-levels">
              <div class="support-level">
                <h3>Critical Issues</h3>
                <ul>
                  <li>Initial Response: < 30 minutes</li>
                  <li>Regular Updates: Every hour</li>
                  <li>Resolution Target: < 4 hours</li>
                </ul>
              </div>

              <div class="support-level">
                <h3>High Priority Issues</h3>
                <ul>
                  <li>Initial Response: < 2 hours</li>
                  <li>Regular Updates: Every 4 hours</li>
                  <li>Resolution Target: < 8 hours</li>
                </ul>
              </div>

              <div class="support-level">
                <h3>Medium Priority Issues</h3>
                <ul>
                  <li>Initial Response: < 4 hours</li>
                  <li>Regular Updates: Daily</li>
                  <li>Resolution Target: < 3 business days</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="section">
            <h2>5. Maintenance Windows</h2>
            <p>
              Scheduled maintenance is performed during the following windows:
            </p>
            <ul>
              <li>Regular Maintenance: Sundays 2:00 AM - 6:00 AM EST</li>
              <li>Emergency Maintenance: As needed with minimum 2-hour notice</li>
            </ul>
          </div>

          <div class="section">
            <h2>6. Security Commitments</h2>
            <ul>
              <li>99.99% successful authentication requests</li>
              <li>24/7 security monitoring and threat detection</li>
              <li>Regular security audits and penetration testing</li>
              <li>Data encryption at rest and in transit</li>
            </ul>
          </div>

          <div class="section">
            <h2>7. Data Recovery</h2>
            <div class="recovery-metrics">
              <div class="metric">
                <span class="label">Recovery Point Objective (RPO)</span>
                <span class="value">15 minutes</span>
              </div>
              <div class="metric">
                <span class="label">Recovery Time Objective (RTO)</span>
                <span class="value">4 hours</span>
              </div>
            </div>
          </div>

          <div class="section">
            <h2>8. Contact Information</h2>
            <div class="contact-info">
              <p>For SLA-related inquiries:</p>
              <ul>
                <li>Email: sla&#64;industryintelligence.com</li>
                <li>Phone: 1-800-555-0123 (24/7 Support)</li>
                <li>Support Portal: support.industryintelligence.com</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .service-agreement {
      padding: 3rem 0;
      background: var(--background-light);
    }

    .container {
      max-width: 1000px;
      margin: 0 auto;
      padding: 0 1rem;
    }

    .page-header {
      text-align: center;
      margin-bottom: 3rem;
    }

    h1 {
      font-size: 2.5rem;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .last-updated {
      color: var(--text-secondary);
      font-style: italic;
    }

    .content {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .section {
      margin-bottom: 2rem;
      padding-bottom: 2rem;
      border-bottom: 1px solid var(--border-color);
    }

    .section:last-child {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: none;
    }

    h2 {
      font-size: 1.5rem;
      color: var(--text-primary);
      margin-bottom: 1.5rem;
    }

    h3 {
      font-size: 1.25rem;
      color: var(--text-primary);
      margin-bottom: 1rem;
    }

    p {
      color: var(--text-secondary);
      line-height: 1.6;
      margin-bottom: 1rem;
    }

    ul {
      list-style: disc;
      margin-left: 1.5rem;
      margin-bottom: 1rem;
    }

    li {
      color: var(--text-secondary);
      line-height: 1.6;
      margin-bottom: 0.5rem;
    }

    .definitions {
      display: grid;
      gap: 2rem;
    }

    .definition {
      background: var(--background-light);
      padding: 1.5rem;
      border-radius: 8px;
    }

    .service-credits {
      overflow-x: auto;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
    }

    th, td {
      padding: 1rem;
      text-align: left;
      border-bottom: 1px solid var(--border-color);
    }

    th {
      background: var(--background-light);
      font-weight: 500;
      color: var(--text-primary);
    }

    td {
      color: var(--text-secondary);
    }

    .support-levels {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
      margin-top: 1rem;
    }

    .support-level {
      background: var(--background-light);
      padding: 1.5rem;
      border-radius: 8px;
    }

    .support-level h3 {
      margin-bottom: 1rem;
      color: var(--primary-color);
    }

    .support-level ul {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .support-level li {
      margin-bottom: 0.5rem;
      padding-left: 1.5rem;
      position: relative;
    }

    .support-level li:before {
      content: "•";
      color: var(--primary-color);
      position: absolute;
      left: 0;
    }

    .recovery-metrics {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.5rem;
      margin-top: 1rem;
    }

    .metric {
      background: var(--background-light);
      padding: 1.5rem;
      border-radius: 8px;
      text-align: center;
    }

    .metric .label {
      display: block;
      color: var(--text-secondary);
      margin-bottom: 0.5rem;
    }

    .metric .value {
      display: block;
      font-size: 1.5rem;
      font-weight: bold;
      color: var(--primary-color);
    }

    .contact-info {
      background: var(--background-light);
      padding: 1.5rem;
      border-radius: 8px;
      margin-top: 1rem;
    }

    @media (max-width: 768px) {
      .content {
        padding: 1.5rem;
      }

      h1 {
        font-size: 2rem;
      }

      h2 {
        font-size: 1.25rem;
      }

      h3 {
        font-size: 1.1rem;
      }

      .support-levels {
        grid-template-columns: 1fr;
      }

      .recovery-metrics {
        grid-template-columns: 1fr;
      }

      table {
        font-size: 0.9rem;
      }

      th, td {
        padding: 0.75rem;
      }
    }
  `]
})
export class ServiceAgreementComponent {
  lastUpdated = new Date('2024-02-24');
} 